import React, { useEffect, useId, useRef, useState } from "react";
import { ImagePlus, Trash2, Upload } from "lucide-react";
import { toast } from "react-toastify";
import { showActionError } from "@/lib/feedback";
import { cn } from "@/lib/cn";
import { initials } from "@/lib/format";
import { LogoCropModal } from "@/components/molecules/LogoCropModal";
import { LOGO_CROP_OUTPUT_SIZE } from "@/lib/cropImage";
import {
  formatLogoMaxSize,
  hasDisplayableLogoUrl,
  isLegacyDataLogoUrl,
  resolveLogoDisplayUrl,
  uploadLogoFile,
  validateLogoFile,
} from "@/lib/logoUpload";

export interface LogoUploadFieldProps {
  label?: string;
  /** Persistent logo reference (`fileUrl`) saved to the API after upload. */
  logoUrl: string | null | undefined;
  /** Optional display URL from the profile API (signed GCS URL). Fallback when `logoUrl` does not resolve. */
  displayLogoUrl?: string | null;
  /** Fallback initials when no logo is set. */
  previewName?: string | null;
  onChange: (logoUrl: string | null) => void | Promise<void>;
  disabled?: boolean;
  errorMessage?: string | null;
  /** Shown below the control when no validation error. */
  hint?: string;
  /**
   * When set, upload/remove show an access popup instead of calling the API
   * (e.g. Support Users who cannot change logos).
   */
  readOnlyMessage?: string;
  className?: string;
}

export const LogoUploadField: React.FC<LogoUploadFieldProps> = ({
  label = "Logo",
  logoUrl,
  displayLogoUrl,
  previewName,
  onChange,
  disabled = false,
  errorMessage,
  hint,
  readOnlyMessage,
  className,
}) => {
  const inputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const skipRemovedResetRef = useRef(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [displayError, setDisplayError] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [cropSource, setCropSource] = useState<{
    objectUrl: string;
    fileName: string;
    fileType: string;
  } | null>(null);

  const clearPreviewBlobUrl = () => {
    setPreviewBlobUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  };

  const resolvedDisplayUrl = removed
    ? null
    : previewBlobUrl ??
      resolveLogoDisplayUrl(logoUrl) ??
      resolveLogoDisplayUrl(displayLogoUrl);

  useEffect(() => {
    setDisplayError(errorMessage ?? localError);
  }, [errorMessage, localError]);

  useEffect(() => {
    if (skipRemovedResetRef.current) {
      skipRemovedResetRef.current = false;
      return;
    }
    setRemoved(false);
  }, [displayLogoUrl, logoUrl]);

  useEffect(() => () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
  }, [previewBlobUrl]);

  useEffect(
    () => () => {
      if (cropSource?.objectUrl) URL.revokeObjectURL(cropSource.objectUrl);
    },
    [cropSource],
  );

  const clearCropSource = () => {
    setCropSource(null);
  };

  const showLogo = hasDisplayableLogoUrl(resolvedDisplayUrl);
  const legacyLogo = isLegacyDataLogoUrl(displayLogoUrl ?? logoUrl);
  const defaultHint =
    hint ??
    `Optional. PNG, JPG, or JPEG up to ${formatLogoMaxSize()}. SVG is not supported. Images are cropped to a ${LOGO_CROP_OUTPUT_SIZE}x${LOGO_CROP_OUTPUT_SIZE} square before upload.`;

  const showReadOnlyPopup = () => {
    if (!readOnlyMessage) return false;
    showActionError(readOnlyMessage, { title: "Access denied" });
    return true;
  };

  const handlePickFile = () => {
    if (showReadOnlyPopup()) return;
    if (!disabled && !busy) {
      fileInputRef.current?.click();
    }
  };

  const uploadPreparedFile = async (file: File) => {
    const validationError = validateLogoFile(file);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    setBusy(true);
    const blobUrl = URL.createObjectURL(file);
    setPreviewBlobUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return blobUrl;
    });
    try {
      const { fileUrl } = await uploadLogoFile(file);
      setRemoved(false);
      await onChange(fileUrl);
      clearPreviewBlobUrl();
    } catch (err: unknown) {
      clearPreviewBlobUrl();
      const status = (err as { status?: number }).status;
      if (status === 403) return;
      setLocalError("Failed to upload the selected image. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    const validationError = validateLogoFile(file);
    if (validationError) {
      setLocalError(validationError);
      return;
    }

    setLocalError(null);
    clearCropSource();
    setCropSource({
      objectUrl: URL.createObjectURL(file),
      fileName: file.name,
      fileType: file.type || "image/jpeg",
    });
  };

  const handleCropCancel = () => {
    clearCropSource();
  };

  const handleCropConfirm = async (file: File) => {
    clearCropSource();
    await uploadPreparedFile(file);
  };

  const handleRemove = async () => {
    if (showReadOnlyPopup()) return;
    if (disabled || busy) return;
    setLocalError(null);
    setBusy(true);
    try {
      skipRemovedResetRef.current = true;
      setRemoved(true);
      clearPreviewBlobUrl();
      await onChange(null);
    } catch (err: unknown) {
      skipRemovedResetRef.current = false;
      setRemoved(false);
      const status = (err as { status?: number }).status;
      if (status === 403) return;
      setLocalError("Failed to remove the logo. Please try again.");
      toast.error("Failed to remove the logo. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label ? (
        <label htmlFor={inputId} className="text-sm font-semibold text-slate-700">
          {label}
        </label>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div
          className={cn(
            "relative shrink-0 w-28 h-28 rounded-2xl border-2 border-dashed overflow-hidden",
            "flex items-center justify-center bg-slate-50",
            showLogo ? "border-slate-200" : "border-slate-300",
          )}
        >
          {showLogo ? (
            <img
              src={resolvedDisplayUrl!}
              alt={`${label} preview`}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-xl font-bold text-slate-400">
              {initials(previewName)}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            id={inputId}
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg"
            className="hidden"
            disabled={disabled || busy}
            onChange={handleFileChange}
          />

          <button
            type="button"
            onClick={handlePickFile}
            disabled={disabled || busy}
            className={cn(
              "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold",
              "border border-[#009BF5] text-[#009BF5] bg-white hover:bg-[#009BF5]/5",
              "transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {showLogo ? (
              <>
                <Upload className="w-4 h-4" />
                {busy ? "Uploading..." : "Replace logo"}
              </>
            ) : (
              <>
                <ImagePlus className="w-4 h-4" />
                {busy ? "Uploading..." : "Upload logo"}
              </>
            )}
          </button>

          {showLogo ? (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled || busy}
              className={cn(
                "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold",
                "border border-slate-200 text-slate-600 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200",
                "transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <Trash2 className="w-4 h-4" />
              Remove logo
            </button>
          ) : null}
        </div>
      </div>

      {displayError ? (
        <p className="text-xs text-red-500 font-medium">{displayError}</p>
      ) : legacyLogo ? (
        <p className="text-xs text-amber-600 font-medium">
          This logo was saved in an old format. Replace it to store a proper file URL in GCS.
        </p>
      ) : readOnlyMessage ? null : (
        <p className="text-xs text-slate-500">{defaultHint}</p>
      )}

      {cropSource ? (
        <LogoCropModal
          open
          imageSrc={cropSource.objectUrl}
          fileName={cropSource.fileName}
          fileType={cropSource.fileType}
          busy={busy}
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      ) : null}
    </div>
  );
};
